import { createTheme, keyframes } from '@mui/material/styles';
import type { ButtonProps } from '@mui/material/Button';
import type { IconButtonProps } from '@mui/material/IconButton';
import { koKR as materialKoKR } from '@mui/material/locale';
import type {} from '@mui/x-data-grid/themeAugmentation';
import { koKR as dataGridKoKR } from '@mui/x-data-grid/locales';
import type { ResolvedBrandTheme } from './brandTheme';
import { createInputSurfaceStyles } from './inputSurface';
import { createFocusRingStyles, createMotionTransition } from './interaction';
import { brandThemeRawColors, designTokens } from './tokens';
import { createChoiceComponents } from './choiceControls';
import { popupSurfaceStyles } from './popupSurface';

const skeletonPulse = keyframes({ from: { opacity: 0.4 }, to: { opacity: 1 } });

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    fill: true;
    weak: true;
  }
}

// PaginationItem은 실행 시 ButtonBase props를 전달하지만 설치 버전의 타입에서 이 속성이 빠져 있습니다.
declare module '@mui/material/PaginationItem' {
  interface PaginationItemOwnProps {
    disableRipple?: boolean;
  }
}

export function createAppTheme(resolved?: ResolvedBrandTheme) {
  const actionColor = resolved?.actionColor ?? brandThemeRawColors.preset.heydayIndigo.primary;
  const actionDarkColor = resolved?.actionDarkColor ?? brandThemeRawColors.default.actionDark;
  const actionSoftColor = resolved?.actionSoftColor ?? brandThemeRawColors.default.actionSoft;
  const actionContrastTextColor = resolved?.actionContrastTextColor ?? brandThemeRawColors.contrast.light;
  const accentColor = resolved?.accentActionColor ?? brandThemeRawColors.preset.heydayIndigo.accent;
  const accentStrongColor = resolved?.accentStrongColor ?? brandThemeRawColors.default.accentStrong;
  const accentSoftColor = resolved?.accentSoftColor ?? brandThemeRawColors.default.accentSoft;
  const accentContrastTextColor = resolved?.accentContrastTextColor ?? brandThemeRawColors.contrast.light;

  const buttonTones = {
    primary: { fill: designTokens.color.action, fillHover: designTokens.color.actionDark, contrast: actionContrastTextColor, weak: designTokens.color.actionSoft, text: designTokens.color.actionDark, hover: designTokens.color.selectedNavigationSurface },
    secondary: { fill: designTokens.color.accent, fillHover: designTokens.color.accentStrong, contrast: designTokens.color.accentContrast, weak: designTokens.color.accentSoft, text: designTokens.color.accentText, hover: designTokens.color.accentSoft },
    inherit: { fill: designTokens.color.primaryText, fillHover: designTokens.color.neutralText, contrast: designTokens.color.inverseText, weak: designTokens.color.subtleSurface, text: designTokens.color.neutralText, hover: designTokens.color.divider },
    info: { fill: designTokens.color.infoDark, fillHover: designTokens.color.infoDark, contrast: designTokens.color.inverseText, weak: designTokens.color.infoSoft, text: designTokens.color.infoDark, hover: designTokens.color.infoSoft },
    success: { fill: designTokens.color.successText, fillHover: designTokens.color.successText, contrast: designTokens.color.inverseText, weak: designTokens.color.successSoft, text: designTokens.color.successText, hover: designTokens.color.successSoft },
    warning: { fill: designTokens.color.warningDark, fillHover: designTokens.color.warningDark, contrast: designTokens.color.inverseText, weak: designTokens.color.warningSoft, text: designTokens.color.warningDark, hover: designTokens.color.warningHoverSoft },
    error: { fill: designTokens.color.dangerText, fillHover: designTokens.color.dangerText, contrast: designTokens.color.inverseText, weak: designTokens.color.dangerSoft, text: designTokens.color.dangerText, hover: designTokens.color.dangerSoft },
  } as const;

  return createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: actionColor,
      dark: actionDarkColor,
      light: actionSoftColor,
      contrastText: actionContrastTextColor,
    },
    secondary: {
      main: accentColor,
      dark: accentStrongColor,
      light: accentSoftColor,
      contrastText: accentContrastTextColor,
    },
    background: {
      default: designTokens.color.canvas,
      paper: designTokens.color.canvas,
    },
    text: {
      primary: designTokens.color.primaryText,
      secondary: designTokens.color.secondaryText,
    },
    divider: resolved?.dividerColor ?? brandThemeRawColors.surface.cool.divider,
    success: { main: designTokens.color.success },
    info: { main: designTokens.color.info },
    warning: { main: designTokens.color.warning },
    error: { main: designTokens.color.dangerText },
  },
  typography: {
    fontFamily: designTokens.typography.fontFamily,
    fontSize: 14,
    h4: { ...designTokens.typography.pageTitle, letterSpacing: '-0.02em' },
    h5: { ...designTokens.typography.sectionTitle, letterSpacing: '-0.02em' },
    h6: { ...designTokens.typography.componentTitle },
    body1: designTokens.typography.body,
    body2: designTokens.typography.ui,
    caption: designTokens.typography.supporting,
    button: { ...designTokens.typography.ui, fontWeight: designTokens.button.fontWeight, textTransform: 'none' },
  },
  spacing: 8,
  transitions: {
    duration: {
      shortest: designTokens.motion.feedback.duration,
      shorter: designTokens.motion.feedback.duration,
      standard: designTokens.motion.expand.duration,
      enteringScreen: designTokens.motion.enter.duration,
      leavingScreen: designTokens.motion.exit.duration,
    },
    easing: {
      easeInOut: designTokens.motion.expand.easing,
      easeOut: designTokens.motion.enter.easing,
      easeIn: designTokens.motion.exit.easing,
    },
  },
  shape: { borderRadius: designTokens.radius.muiBase },
  components: {
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          height: designTokens.progress.barHeight,
          borderRadius: designTokens.radius.pill,
          backgroundColor: designTokens.color.subtleSurface,
          '@media (forced-colors: active)': { outline: '1px solid CanvasText', outlineOffset: '-1px' },
        },
        bar: {
          borderRadius: 'inherit',
          '@media (forced-colors: active)': { backgroundColor: 'Highlight' },
        },
      },
    },
    MuiSkeleton: {
      styleOverrides: {
        root: {
          backgroundColor: designTokens.color.subtleSurface,
          '@media (forced-colors: active)': { outline: '1px solid CanvasText', outlineOffset: '-1px' },
        },
        rounded: { borderRadius: designTokens.loading.skeleton.shapeRadius },
        pulse: {
          animation: `${skeletonPulse} ${designTokens.loading.skeleton.duration}ms linear infinite alternate`,
          '@media (prefers-reduced-motion: reduce)': { animation: 'none !important', opacity: 1 },
        },
      },
    },
    ...createChoiceComponents(actionContrastTextColor),
    MuiCssBaseline: {
      styleOverrides: {
        'html, body, #root': { minHeight: '100%', backgroundColor: designTokens.color.canvas },
        '*:focus-visible': { scrollMargin: 8 },
        '@media (prefers-reduced-motion: reduce)': {
          '*, *::before, *::after': { animationDuration: '0.01ms !important', animationIterationCount: '1 !important', transitionDuration: '0.01ms !important', scrollBehavior: 'auto !important' },
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true, disableRipple: true, size: 'medium' },
      styleOverrides: {
        root: {
          borderRadius: designTokens.radius.button,
          fontWeight: designTokens.button.fontWeight,
          transition: ['background-color', 'border-color', 'color', 'box-shadow'].map((property) => `${property} ${designTokens.button.feedbackDuration}ms linear`).join(', '),
          '&:active': { transitionDuration: `${designTokens.button.pressedDuration}ms` },
          '&.Mui-focusVisible': { ...createFocusRingStyles(), boxShadow: `0 0 0 2px ${designTokens.color.canvas}` },
          '&.Mui-disabled:not(.MuiButton-loading)': { opacity: designTokens.button.disabledOpacity },
          '& .MuiButton-startIcon, & .MuiButton-endIcon': { margin: 0, flexShrink: 0 },
          '&.MuiButton-loading.MuiButton-loadingPositionCenter': {
            color: 'transparent',
          },
          variants: [
            ...Object.entries(buttonTones).flatMap(([color, tone]) => {
              const quietTone = color === 'primary' || color === 'secondary' || color === 'inherit' ? buttonTones.inherit : tone;
              return [
              ...(['fill', 'contained'] as const).map((variant) => ({
                props: { variant, color: color as ButtonProps['color'] },
                style: {
                  border: '1px solid transparent',
                  color: tone.contrast,
                  backgroundColor: tone.fill,
                  boxShadow: 'none',
                  '&:hover': { backgroundColor: color === 'primary' || color === 'secondary' ? tone.fillHover : `color-mix(in srgb, ${tone.fill} 90%, ${designTokens.color.primaryText})` },
                  '&:active': { backgroundColor: `color-mix(in srgb, ${tone.fillHover} 80%, ${designTokens.color.primaryText})` },
                  '&.Mui-disabled': { borderColor: 'transparent', color: tone.contrast, backgroundColor: tone.fill },
                  '& .MuiButton-loadingIndicator': { color: tone.contrast },
                },
              })),
              {
                props: { variant: 'weak' as const, color: color as ButtonProps['color'] },
                style: {
                  border: '1px solid transparent',
                  color: tone.text,
                  backgroundColor: tone.weak,
                  boxShadow: 'none',
                  '&:hover': { backgroundColor: tone.hover },
                  '&:active': { backgroundColor: `color-mix(in srgb, ${tone.hover} 88%, ${tone.text})` },
                  '&.Mui-disabled': { borderColor: 'transparent', color: tone.text, backgroundColor: tone.weak },
                  '& .MuiButton-loadingIndicator': { color: tone.text },
                },
              },
              {
                props: { variant: 'outlined' as const, color: color as ButtonProps['color'] },
                style: {
                  border: `1px solid ${designTokens.color.divider}`,
                  color: color === 'primary' ? designTokens.color.primaryText : tone.text,
                  backgroundColor: 'transparent',
                  boxShadow: 'none',
                  '&:hover': { borderColor: color === 'inherit' || color === 'primary' ? designTokens.color.secondaryText : tone.text, backgroundColor: 'transparent' },
                  '&:active': { borderColor: color === 'inherit' || color === 'primary' ? designTokens.color.primaryText : tone.text, backgroundColor: 'transparent' },
                  '&.Mui-disabled': { borderColor: designTokens.color.divider, color: color === 'primary' ? designTokens.color.primaryText : tone.text, backgroundColor: 'transparent' },
                  '& .MuiButton-loadingIndicator': { color: color === 'primary' ? designTokens.color.primaryText : tone.text },
                },
              },
              {
                props: { variant: 'text' as const, color: color as ButtonProps['color'] },
                style: {
                  border: '1px solid transparent',
                  color: quietTone.text,
                  backgroundColor: quietTone.weak,
                  boxShadow: 'none',
                  '&:hover': { backgroundColor: quietTone.hover },
                  '&:active': { backgroundColor: `color-mix(in srgb, ${quietTone.hover} 88%, ${quietTone.text})` },
                  '&.Mui-disabled': { borderColor: 'transparent', color: quietTone.text, backgroundColor: quietTone.weak },
                  '& .MuiButton-loadingIndicator': { color: quietTone.text },
                },
              },
            ];
            }),
            ...(['small', 'medium', 'large'] as const).map((size) => {
              const metrics = designTokens.button.sizes[size];
              return {
                props: { size },
                style: {
                  minHeight: metrics.height,
                  minWidth: metrics.minWidth,
                  paddingBlock: 0,
                  paddingInline: metrics.paddingInline,
                  fontSize: metrics.fontSize,
                  lineHeight: metrics.lineHeight,
                  gap: metrics.iconGap,
                  '&:has(> .MuiButton-startIcon)': { paddingInlineStart: metrics.iconInset },
                  '&:has(> .MuiButton-endIcon)': { paddingInlineEnd: metrics.iconInset },
                  '& .MuiButton-startIcon > *:nth-of-type(1), & .MuiButton-endIcon > *:nth-of-type(1)': { fontSize: metrics.iconSize },
                  [designTokens.media.touchTarget]: { minHeight: Math.max(metrics.height, designTokens.layout.touchTargetMinSize) },
                },
              };
            }),
            ...(['small', 'medium', 'large'] as const).map((size) => ({
              props: { size, variant: 'text' as const },
              style: {
                '&:has(> .MuiButton-startIcon)': { paddingInlineStart: designTokens.space.xs, paddingInlineEnd: designTokens.space.sm },
                '&:has(> .MuiButton-endIcon)': { paddingInlineStart: designTokens.space.sm, paddingInlineEnd: designTokens.space.xs },
              },
            })),
          ],
        },
      },
    },
    MuiIconButton: {
      defaultProps: { disableRipple: true, size: 'medium' },
      styleOverrides: {
        root: {
          borderRadius: designTokens.radius.circle,
          padding: 0,
          flexShrink: 0,
          transition: ['background-color', 'border-color', 'color', 'box-shadow'].map((property) => `${property} ${designTokens.button.feedbackDuration}ms linear`).join(', '),
          '&:hover': { backgroundColor: designTokens.color.hoverSurface },
          '&:active': { backgroundColor: `color-mix(in srgb, currentColor 12%, transparent)`, transitionDuration: `${designTokens.button.pressedDuration}ms` },
          '&.Mui-focusVisible': { ...createFocusRingStyles(), boxShadow: `0 0 0 2px ${designTokens.color.canvas}` },
          '&.Mui-disabled:not(.MuiIconButton-loading)': { opacity: designTokens.button.disabledOpacity },
          '&.MuiIconButton-loading > :not(.MuiIconButton-loadingWrapper)': { visibility: 'hidden' },
          '& > .MuiSvgIcon-root': { fontSize: 'inherit' },
          variants: [
            ...Object.entries({
              default: designTokens.color.secondaryText,
              inherit: 'inherit',
              primary: designTokens.color.action,
              secondary: designTokens.color.accentText,
              info: designTokens.color.infoDark,
              success: designTokens.color.successText,
              warning: designTokens.color.warningDark,
              error: designTokens.color.dangerText,
            }).map(([color, foreground]) => ({
              props: { color: color as IconButtonProps['color'] },
              style: { color: foreground, '&.Mui-disabled': { color: foreground }, '& .MuiIconButton-loadingIndicator': { color: foreground } },
            })),
            ...(['small', 'medium', 'large'] as const).map((size) => {
              const metrics = designTokens.button.sizes[size];
              return {
                props: { size },
                style: {
                  width: metrics.height,
                  height: metrics.height,
                  fontSize: metrics.iconSize,
                  [designTokens.media.touchTarget]: {
                    minHeight: Math.max(metrics.height, designTokens.layout.touchTargetMinSize),
                    minWidth: Math.max(metrics.height, designTokens.layout.touchTargetMinSize),
                  },
                },
              };
            }),
          ],
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          minHeight: designTokens.layout.controlMinHeight,
          minWidth: designTokens.layout.controlMinHeight,
          borderRadius: designTokens.radius.button,
          borderColor: designTokens.color.divider,
          color: designTokens.color.secondaryText,
          transition: createMotionTransition(['background-color', 'border-color', 'color']),
          [designTokens.media.touchTarget]: { minHeight: designTokens.layout.touchTargetMinSize, minWidth: designTokens.layout.touchTargetMinSize },
          '&:hover': { backgroundColor: designTokens.color.hoverSurface },
          '&.Mui-selected': { backgroundColor: designTokens.color.compactSelectedSurface, color: designTokens.color.actionDark },
          '&.Mui-selected:hover': { backgroundColor: designTokens.color.selectedHoverSurface },
          '&.Mui-focusVisible': createFocusRingStyles({ inset: true }),
          '&.Mui-disabled': { borderColor: designTokens.color.divider, color: designTokens.color.disabledText, backgroundColor: 'transparent' },
        },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: { backgroundImage: 'none' },
        rounded: { borderRadius: designTokens.radius.surface },
      },
    },
    MuiAccordion: {
      defaultProps: { disableGutters: true, elevation: 0 },
      styleOverrides: {
        root: {
          padding: `${designTokens.space.xs}px`,
          backgroundColor: 'transparent',
          boxShadow: 'none',
          '&::before': { display: 'none' },
          '&.Mui-disabled': { backgroundColor: 'transparent', opacity: designTokens.button.disabledOpacity },
        },
      },
    },
    MuiAccordionSummary: {
      defaultProps: { disableRipple: true },
      styleOverrides: {
        root: {
          minHeight: designTokens.layout.touchTargetMinSize,
          padding: `${designTokens.space.xs}px`,
          borderRadius: designTokens.radius.navigation,
          color: designTokens.color.primaryText,
          '&.Mui-disabled': { opacity: 1 },
          '&:hover': { backgroundColor: designTokens.color.hoverSurface },
          '&.Mui-focusVisible, &:focus-visible': createFocusRingStyles({ inset: true }),
        },
        content: {
          margin: 0,
          padding: `2px ${designTokens.space.xs}px`,
          minWidth: 0,
          overflowWrap: 'anywhere',
        },
        expandIconWrapper: { color: designTokens.color.secondaryText, '& svg': { fontSize: 20 } },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: { root: { padding: `${designTokens.space.xs}px`, minWidth: 0 } },
    },
    MuiDialog: {
      styleOverrides: {
        backdrop: { backgroundColor: designTokens.overlay.modalBackdrop },
        paper: { borderRadius: designTokens.radius.dialog, boxShadow: designTokens.shadow.overlay },
        paperFullScreen: { borderRadius: 0 },
      },
    },
    MuiPopover: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        paper: popupSurfaceStyles('dialog'),
      },
    },
    MuiAutocomplete: {
      defaultProps: {
        size: 'small',
        slotProps: {
          clearIndicator: { size: 'medium', tabIndex: 0 },
          popupIndicator: { size: 'medium' },
          popper: { modifiers: [{ name: 'offset', options: { offset: [0, 8] } }] },
        },
      },
      styleOverrides: {
        root: ({ ownerState }) => ({
          '--heyday-autocomplete-action-size': `${designTokens.layout.controlMinHeight}px`,
          '--heyday-autocomplete-action-count': 0,
          '--heyday-autocomplete-loading-width': '0px',
          [designTokens.media.touchTarget]: { '--heyday-autocomplete-action-size': `${designTokens.layout.touchTargetMinSize}px` },
          '&.MuiAutocomplete-hasPopupIcon, &.MuiAutocomplete-hasClearIcon': {
            '--heyday-autocomplete-action-count': 1,
          },
          '&.MuiAutocomplete-hasPopupIcon.MuiAutocomplete-hasClearIcon': {
            '--heyday-autocomplete-action-count': 2,
          },
          '&[data-loading="true"]': { '--heyday-autocomplete-loading-width': '24px' },
          // 입력에서 지우기로 Tab 이동해도 MUI의 blur 처리로 버튼이 숨지 않게 합니다.
          '&:focus-within .MuiAutocomplete-clearIndicator': { visibility: 'visible' },
          // MUI의 sizeSmall 전용 padding보다 구체적으로 일반 입력 규격을 적용합니다.
          '&& .MuiAutocomplete-inputRoot.MuiOutlinedInput-root': {
            minHeight: ownerState.size === 'medium' ? 48 : 'var(--heyday-autocomplete-action-size)',
            padding: ownerState.multiple ? 4 : 0,
            paddingLeft: ownerState.multiple ? designTokens.layout.dropdownInlineInset - designTokens.space.xxs : 0,
            paddingRight: 'calc(var(--heyday-autocomplete-action-count) * var(--heyday-autocomplete-action-size) + var(--heyday-autocomplete-loading-width))',
            '& .MuiAutocomplete-input': {
              paddingBlock: ownerState.multiple ? designTokens.space.xxs : designTokens.space.xs,
              paddingInline: ownerState.multiple ? designTokens.space.xxs : designTokens.layout.dropdownInlineInset,
            },
          },
          '& .HeydayAutocomplete-actions': {
            position: 'absolute', insetInlineEnd: 0, top: 0, bottom: 0,
            display: 'inline-flex', alignItems: 'center',
          },
          '& .HeydayAutocomplete-loading': {
            width: 'var(--heyday-autocomplete-loading-width)', flexShrink: 0,
            display: 'inline-flex', justifyContent: 'center', alignItems: 'center',
          },
          '& .MuiAutocomplete-tag': { margin: 4, maxWidth: 'calc(100% - 8px)' },
        }),
        endAdornment: { position: 'static', transform: 'none', display: 'inline-flex', alignItems: 'center' },
        clearIndicator: { marginRight: 0, padding: 0, '&.Mui-focusVisible': createFocusRingStyles({ inset: true }) },
        popupIndicator: { marginRight: 0, padding: 0, '&.Mui-focusVisible': createFocusRingStyles({ inset: true }) },
        paper: popupSurfaceStyles('menu'),
        listbox: ({ theme }) => ({
          padding: designTokens.layout.dropdownListInset, display: 'flex', flexDirection: 'column', gap: designTokens.layout.menuItemSpacing,
          '&& .MuiAutocomplete-option': {
            minHeight: designTokens.layout.controlMinHeight,
            [theme.breakpoints.up('sm')]: { minHeight: designTokens.layout.controlMinHeight },
            flexShrink: 0,
            paddingBlock: designTokens.layout.menuItemBlockInset,
            paddingInline: designTokens.layout.dropdownOptionInlineInset,
            gap: designTokens.layout.menuItemGap,
            borderRadius: designTokens.radius.menuItem,
            ...designTokens.typography.ui,
            fontWeight: 400,
            overflowWrap: 'anywhere',
            transition: createMotionTransition(['background-color', 'color'], 'menu'),
            [designTokens.media.touchTarget]: { minHeight: designTokens.layout.touchTargetMinSize },
            '&.Mui-focused': { backgroundColor: designTokens.color.hoverSurface },
            '&[aria-selected="true"]': { backgroundColor: designTokens.color.compactSelectedSurface, color: designTokens.color.actionDark },
            '&[aria-selected="true"].Mui-focused': { backgroundColor: designTokens.color.selectedNavigationSurface },
            '&.Mui-focusVisible': { ...createFocusRingStyles({ inset: true }), backgroundColor: 'transparent' },
            '&[aria-selected="true"].Mui-focusVisible': { backgroundColor: designTokens.color.compactSelectedSurface },
            '&[aria-disabled="true"]': { opacity: designTokens.button.disabledOpacity },
          },
        }),
        loading: { padding: '12px 16px', ...designTokens.typography.ui, color: designTokens.color.secondaryText },
        noOptions: { padding: '12px 16px', ...designTokens.typography.ui, color: designTokens.color.secondaryText },
      },
    },
    MuiSelect: {
      defaultProps: {
        MenuProps: {
          anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
          transformOrigin: { vertical: -designTokens.layout.menuAnchorGap, horizontal: 'center' },
          slotProps: {
            // 스크롤바 보정이 list의 inline padding을 바꿔도 양쪽 여백은 유지합니다.
            paper: { sx: { p: `${designTokens.layout.dropdownListInset}px`, maxHeight: 'min(300px, calc(100dvh - 32px))', maxWidth: 'calc(100vw - 32px)' } },
            list: { sx: { p: 0, '& .MuiMenuItem-root': { paddingInline: `${designTokens.layout.dropdownOptionInlineInset}px` } } },
          },
        },
      },
      styleOverrides: {
        outlined: {
          '&&': {
            paddingBlock: designTokens.space.xs,
            paddingInlineStart: designTokens.layout.dropdownInlineInset,
            paddingRight: designTokens.layout.controlMinHeight + designTokens.layout.dropdownIndicatorGap,
          },
          [designTokens.media.touchTarget]: { '&&': { paddingRight: designTokens.layout.touchTargetMinSize + designTokens.layout.dropdownIndicatorGap } },
        },
        icon: ({ ownerState }) => ownerState.variant === 'outlined' ? {
          fontSize: designTokens.layout.menuItemIconSize,
          right: (designTokens.layout.controlMinHeight - designTokens.layout.menuItemIconSize) / 2,
          [designTokens.media.touchTarget]: { right: (designTokens.layout.touchTargetMinSize - designTokens.layout.menuItemIconSize) / 2 },
        } : {},
      },
    },
    MuiMenu: {
      defaultProps: {
        elevation: 0,
        anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
        transformOrigin: { vertical: -designTokens.layout.menuAnchorGap, horizontal: 'left' },
      },
      styleOverrides: {
        paper: {
          ...popupSurfaceStyles('menu'),
          minWidth: 160,
          maxWidth: `calc(100vw - ${designTokens.layout.popupViewportInset * 2}px)`,
          maxHeight: '60vh',
        },
        list: { padding: designTokens.space.xs, display: 'flex', flexDirection: 'column', gap: designTokens.layout.menuItemSpacing },
      },
    },
    MuiTooltip: {
      defaultProps: { enterDelay: 300, leaveDelay: 100, placement: 'top' },
      styleOverrides: {
        tooltip: ({ ownerState }) => ({
          ...designTokens.typography.ui,
          fontWeight: 400,
          padding: `${designTokens.layout.tooltipBlockInset}px ${designTokens.layout.tooltipInlineInset}px`,
          color: designTokens.color.primaryText,
          backgroundColor: designTokens.color.canvas,
          border: `1px solid ${designTokens.color.divider}`,
          borderRadius: designTokens.radius.tooltip,
          boxShadow: designTokens.shadow.tooltip,
          maxWidth: `min(320px, calc(100vw - ${designTokens.layout.popupViewportInset * 2}px))`,
          overflowWrap: 'anywhere',
          margin: 0,
          // MUI의 기본 14px(터치 24px) 여백도 같은 위치 규칙으로 덮어씁니다.
          '.MuiTooltip-popper[data-popper-placement*="left"] &': { marginInlineEnd: ownerState.arrow ? designTokens.layout.tooltipArrowAnchorGap : designTokens.layout.tooltipAnchorGap },
          '.MuiTooltip-popper[data-popper-placement*="right"] &': { marginInlineStart: ownerState.arrow ? designTokens.layout.tooltipArrowAnchorGap : designTokens.layout.tooltipAnchorGap },
          '.MuiTooltip-popper[data-popper-placement*="top"] &': { marginBottom: ownerState.arrow ? designTokens.layout.tooltipArrowAnchorGap : designTokens.layout.tooltipAnchorGap },
          '.MuiTooltip-popper[data-popper-placement*="bottom"] &': { marginTop: ownerState.arrow ? designTokens.layout.tooltipArrowAnchorGap : designTokens.layout.tooltipAnchorGap },
          '@media (forced-colors: active)': { outline: '1px solid CanvasText' },
        }),
        // 기존 탐색 tooltip의 방향 표시 변형은 유지합니다.
        arrow: {
          color: designTokens.color.canvas,
          '&::before': { border: `1px solid ${designTokens.color.divider}`, boxSizing: 'border-box' },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: ({ theme }) => ({
          minHeight: designTokens.layout.controlMinHeight,
          [theme.breakpoints.up('sm')]: { minHeight: designTokens.layout.controlMinHeight },
          [designTokens.media.touchTarget]: { minHeight: designTokens.layout.touchTargetMinSize },
          margin: 0,
          paddingBlock: designTokens.layout.menuItemBlockInset,
          paddingInlineStart: designTokens.layout.menuItemInlineInset,
          paddingInlineEnd: designTokens.layout.menuItemEndInset,
          gap: designTokens.layout.menuItemGap,
          borderRadius: designTokens.radius.menuItem,
          ...designTokens.typography.ui,
          fontWeight: 400,
          whiteSpace: 'normal',
          overflowWrap: 'anywhere',
          transition: createMotionTransition(['background-color', 'color'], 'menu'),
          '& .MuiListItemIcon-root': { minWidth: designTokens.layout.menuItemIconSize, color: 'inherit' },
          '& .MuiListItemIcon-root > .MuiSvgIcon-root': { fontSize: designTokens.layout.menuItemIconSize },
          '&:hover:not(.Mui-disabled)': { backgroundColor: designTokens.color.hoverSurface },
          '&.Mui-selected': { backgroundColor: designTokens.color.compactSelectedSurface, color: designTokens.color.actionDark },
          '&.Mui-selected:hover, &.Mui-selected.Mui-focusVisible:hover': { backgroundColor: designTokens.color.selectedNavigationSurface },
          '&.Mui-focusVisible': { ...createFocusRingStyles({ inset: true }), backgroundColor: 'transparent' },
          '&.Mui-selected.Mui-focusVisible': { backgroundColor: designTokens.color.compactSelectedSurface },
          '&.Mui-disabled': { opacity: designTokens.button.disabledOpacity },
        }),
      },
    },
    MuiChip: {
      defaultProps: { size: 'small' },
      styleOverrides: {
        root: ({ ownerState }) => ({
          height: designTokens.layout.valueBadgeHeight,
          borderRadius: designTokens.layout.chipRadius,
          padding: '2px 8px',
          gap: designTokens.space.xxs,
          fontWeight: 400,
          ...designTokens.typography.supporting,
          ...(ownerState.variant === 'outlined' ? { borderColor: designTokens.color.divider } : {}),
          '&.Mui-focusVisible': createFocusRingStyles({ inset: true }),
          '&.Mui-disabled': { opacity: designTokens.button.disabledOpacity },
          '@media (forced-colors: active)': { border: '1px solid CanvasText' },
        }),
        label: { padding: 0 },
        icon: { margin: 0, fontSize: designTokens.layout.valueBadgeIconSize },
        deleteIcon: { margin: 0, fontSize: designTokens.layout.valueBadgeIconSize },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: ({ ownerState, theme }) => ({
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr)',
          gridTemplateAreas: '"message"',
          '&:has(> .MuiAlert-action)': { gridTemplateAreas: '"message" "action"' },
          '&:has(> .MuiAlert-icon)': {
            gridTemplateColumns: `${designTokens.feedback.notice.iconSize}px minmax(0, 1fr)`,
            gridTemplateAreas: '"icon message"',
          },
          '&:has(> .MuiAlert-icon):has(> .MuiAlert-action)': { gridTemplateAreas: '"icon message" ". action"' },
          [theme.breakpoints.up('sm')]: {
            '&:has(> .MuiAlert-action)': {
              gridTemplateColumns: 'minmax(0, 1fr) auto',
              gridTemplateAreas: '"message action"',
            },
            '&:has(> .MuiAlert-icon):has(> .MuiAlert-action)': {
              gridTemplateColumns: `${designTokens.feedback.notice.iconSize}px minmax(0, 1fr) auto`,
              gridTemplateAreas: '"icon message action"',
            },
          },
          alignItems: 'start',
          columnGap: designTokens.feedback.notice.gap,
          padding: designTokens.feedback.notice.inset,
          borderRadius: designTokens.feedback.notice.radius,
          boxShadow: 'none',
          ...designTokens.typography.ui,
          fontWeight: 400,
          ...(ownerState.variant === 'standard' ? { color: designTokens.color.primaryText } : {}),
          '@media (forced-colors: active)': { outline: '1px solid CanvasText' },
        }),
        icon: {
          gridArea: 'icon', margin: 0, padding: 0, opacity: 1,
          fontSize: designTokens.feedback.notice.iconSize,
          '& .MuiSvgIcon-root': { fontSize: 'inherit' },
        },
        message: { gridArea: 'message', minWidth: 0, padding: 0, overflow: 'visible', overflowWrap: 'anywhere' },
        action: ({ theme }) => ({
          gridArea: 'action', minWidth: 0, padding: 0, margin: 0,
          marginTop: designTokens.space.xs,
          [theme.breakpoints.up('sm')]: { marginTop: 0 },
          display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: designTokens.space.xs,
        }),
      },
    },
    MuiAlertTitle: {
      styleOverrides: {
        root: { ...designTokens.typography.ui, fontWeight: 500, margin: 0, marginBottom: designTokens.space.xxs },
      },
    },
    MuiBadge: {
      styleOverrides: {
        badge: ({ ownerState }) => ownerState.variant === 'dot' ? {} : {
          minWidth: designTokens.layout.countBadgeSize,
          height: designTokens.layout.countBadgeSize,
          padding: `0 ${designTokens.space.xxs}px`,
          ...designTokens.typography.supporting,
          fontWeight: 500,
          fontVariantNumeric: 'tabular-nums',
          borderRadius: designTokens.shape.full,
          '@media (forced-colors: active)': { border: '1px solid CanvasText' },
        },
      },
    },
    MuiTextField: {
      defaultProps: { size: 'small' },
    },
    MuiInputAdornment: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          flexShrink: 0,
          color: designTokens.color.secondaryText,
          font: 'inherit',
          whiteSpace: 'nowrap',
          marginLeft: 0,
          marginRight: 0,
          marginInlineStart: ownerState.position === 'end' ? designTokens.layout.inputAdornmentGap : 0,
          marginInlineEnd: ownerState.position === 'start' ? designTokens.layout.inputAdornmentGap : 0,
          '& .MuiTypography-root': { font: 'inherit', color: 'inherit' },
        }),
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          ...createInputSurfaceStyles({ variant: 'notched', disabled: ownerState.disabled, invalid: ownerState.error }),
          ...designTokens.typography.ui,
          fontWeight: 400,
          color: designTokens.color.primaryText,
          backgroundColor: designTokens.color.canvas,
          minHeight: ownerState.multiline ? 64 : ownerState.size === 'small' ? 40 : 48,
          padding: ownerState.multiline ? designTokens.layout.inputInset : 0,
          // 장식이 있는 쪽은 입력 그룹이 바깥 여백을 소유하고 input은 중복하지 않습니다.
          paddingInlineStart: ownerState.multiline || ownerState.startAdornment ? designTokens.layout.inputInset : 0,
          paddingInlineEnd: ownerState.multiline || ownerState.endAdornment ? designTokens.layout.inputInset : 0,
          [designTokens.media.touchTarget]: {
            minHeight: ownerState.multiline ? 64 : ownerState.size === 'small' ? designTokens.layout.touchTargetMinSize : 48,
            fontSize: designTokens.typography.body.fontSize,
          },
          '&.Mui-disabled': {
            opacity: designTokens.button.disabledOpacity,
            color: designTokens.color.primaryText,
            // disabled 감쇠는 입력 그룹에서 한 번만 적용합니다.
            '& .MuiIconButton-root.Mui-disabled': { opacity: 1, color: 'inherit' },
            '& .MuiSelect-icon.Mui-disabled': { color: 'inherit' },
          },
        }),
        input: ({ ownerState }) => ({
          padding: ownerState.multiline ? 0 : designTokens.layout.inputInset,
          paddingInlineStart: ownerState.multiline || ownerState.startAdornment ? 0 : designTokens.layout.inputInset,
          paddingInlineEnd: ownerState.multiline || ownerState.endAdornment ? 0 : designTokens.layout.inputInset,
          font: 'inherit',
          '&::placeholder': { color: designTokens.color.mutedText, opacity: 1 },
          '&.Mui-disabled': { WebkitTextFillColor: 'inherit' },
        }),
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          marginInline: 0,
          marginTop: 4,
          ...designTokens.typography.supporting,
          fontWeight: 400,
          color: designTokens.color.secondaryText,
          '&.Mui-error': { color: designTokens.color.dangerText },
          '&.Mui-disabled': { color: designTokens.color.secondaryText },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          minHeight: designTokens.layout.sectionSidebarItemHeight,
          [designTokens.media.touchTarget]: { minHeight: designTokens.layout.touchTargetMinSize },
          borderRadius: designTokens.radius.navigation,
          transition: createMotionTransition(['background-color', 'color']),
          '&:hover': { backgroundColor: designTokens.color.hoverSurface },
          '&.Mui-selected': { backgroundColor: designTokens.color.compactSelectedSurface },
          '&.Mui-selected:hover, &.Mui-selected.Mui-focusVisible:hover': { backgroundColor: designTokens.color.selectedNavigationSurface },
          '&.Mui-focusVisible, &:focus-visible': { ...createFocusRingStyles({ inset: true }), backgroundColor: 'transparent' },
          '&.Mui-selected.Mui-focusVisible': { backgroundColor: designTokens.color.compactSelectedSurface },
        },
      },
    },
    MuiPagination: {
      styleOverrides: {
        ul: { gap: designTokens.space.xxs },
      },
    },
    MuiPaginationItem: {
      defaultProps: { disableRipple: true },
      styleOverrides: {
        root: {
          ...designTokens.typography.ui,
          fontWeight: 400,
          fontVariantNumeric: 'tabular-nums',
          minWidth: designTokens.layout.pageHeaderControlSize,
          height: designTokens.layout.pageHeaderControlSize,
          padding: 0,
          margin: 0,
          border: '1px solid transparent',
          borderRadius: designTokens.radius.circle,
          color: designTokens.color.secondaryText,
          transition: createMotionTransition(['background-color', 'color']),
          [designTokens.media.touchTarget]: { minWidth: designTokens.layout.touchTargetMinSize, height: designTokens.layout.touchTargetMinSize },
          '&:hover': { backgroundColor: designTokens.color.hoverSurface },
          '&:active': { backgroundColor: designTokens.color.selectedNavigationSurface },
          '&.Mui-selected': { fontWeight: 700, color: designTokens.color.actionDark, backgroundColor: designTokens.color.compactSelectedSurface },
          '&.Mui-selected:hover': { backgroundColor: designTokens.color.selectedNavigationSurface },
          '&.Mui-focusVisible': { ...createFocusRingStyles(), backgroundColor: 'transparent' },
          '&.Mui-selected.Mui-focusVisible': { backgroundColor: designTokens.color.compactSelectedSurface },
          '&.Mui-disabled': { opacity: designTokens.button.disabledOpacity },
          '&.Mui-selected.Mui-disabled': { opacity: designTokens.button.disabledOpacity, color: designTokens.color.actionDark, backgroundColor: designTokens.color.compactSelectedSurface },
          '@media (hover: none)': {
            '&:hover': { backgroundColor: 'transparent' },
            '&.Mui-selected:hover': { backgroundColor: designTokens.color.compactSelectedSurface },
          },
          '@media (forced-colors: active)': { '&.Mui-selected': { borderColor: 'Highlight' } },
        },
        icon: { fontSize: designTokens.button.sizes.medium.iconSize },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: 0,
          '& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeader .MuiDataGrid-sortButton, & .MuiDataGrid-columnHeaders .MuiDataGrid-filler, & .MuiDataGrid-columnHeaders .MuiDataGrid-scrollbarFiller': {
            backgroundColor: `${designTokens.color.tableHeaderSurface} !important`,
          },
          '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
            outline: 'none',
          },
          '& .MuiDataGrid-columnHeader:focus-visible, & .MuiDataGrid-cell:focus-visible': createFocusRingStyles({ inset: true }),
        },
        columnHeaders: { backgroundColor: designTokens.color.tableHeaderSurface },
        columnHeader: { paddingInline: designTokens.layout.tableCellInlineInset },
        columnHeaderTitle: { ...designTokens.typography.ui, fontWeight: 500 },
        row: {
          backgroundColor: designTokens.color.canvas,
          '&:hover': { backgroundColor: designTokens.color.hoverSurface },
          '&.Mui-selected': { backgroundColor: designTokens.color.compactSelectedSurface },
          '&.Mui-selected:hover': { backgroundColor: designTokens.color.selectedNavigationSurface },
          '@media (hover: none)': {
            '&:hover': { backgroundColor: designTokens.color.canvas },
            '&.Mui-selected:hover': { backgroundColor: designTokens.color.compactSelectedSurface },
          },
          '@media (forced-colors: active)': {
            '&.Mui-selected, &.Mui-selected:hover': { outline: '1px solid Highlight', outlineOffset: -1 },
          },
        },
        cell: { display: 'flex', alignItems: 'center', paddingInline: designTokens.layout.tableCellInlineInset, ...designTokens.typography.ui, borderColor: designTokens.color.tableCellDivider },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          border: `1px solid ${designTokens.color.tableCellDivider}`,
          borderRadius: designTokens.radius.table,
          backgroundColor: designTokens.color.canvas,
        },
      },
    },
    MuiTableBody: {
      styleOverrides: { root: { '& > tr:last-child > td, & > tr:last-child > th': { borderBottom: 0 } } },
    },
    MuiTableHead: {
      styleOverrides: { root: { backgroundColor: designTokens.color.tableHeaderSurface } },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${designTokens.color.tableCellDivider}`,
          padding: `${designTokens.space.xs}px ${designTokens.layout.tableCellInlineInset}px`,
          ...designTokens.typography.ui,
          color: designTokens.color.primaryText,
          overflowWrap: 'anywhere',
        },
        head: {
          backgroundColor: designTokens.color.tableHeaderSurface,
          borderBottomColor: designTokens.color.tableCellDivider,
          paddingBlock: designTokens.space.md,
          fontWeight: 500,
        },
        stickyHeader: { backgroundColor: designTokens.color.tableHeaderSurface },
      },
    },
  },
  }, materialKoKR, dataGridKoKR);
}

export const theme = createAppTheme();
